/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const ORACLE_CONSULT_DISCRIMINATOR = new Uint8Array([
  239, 237, 255, 177, 142, 72, 96, 175,
]);

export function getOracleConsultDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ORACLE_CONSULT_DISCRIMINATOR,
  );
}

export type OracleConsultInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountOracle extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? ReadonlyAccount<TAccountPool>
        : TAccountPool,
      TAccountOracle extends string
        ? ReadonlyAccount<TAccountOracle>
        : TAccountOracle,
      ...TRemainingAccounts,
    ]
  >;

export type OracleConsultInstructionData = {
  discriminator: ReadonlyUint8Array;
  windowSeconds: number;
};

export type OracleConsultInstructionDataArgs = { windowSeconds: number };

export function getOracleConsultInstructionDataEncoder(): FixedSizeEncoder<OracleConsultInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['windowSeconds', getU32Encoder()],
    ]),
    (value) => ({ ...value, discriminator: ORACLE_CONSULT_DISCRIMINATOR }),
  );
}

export function getOracleConsultInstructionDataDecoder(): FixedSizeDecoder<OracleConsultInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['windowSeconds', getU32Decoder()],
  ]);
}

export function getOracleConsultInstructionDataCodec(): FixedSizeCodec<
  OracleConsultInstructionDataArgs,
  OracleConsultInstructionData
> {
  return combineCodec(
    getOracleConsultInstructionDataEncoder(),
    getOracleConsultInstructionDataDecoder(),
  );
}

export type OracleConsultAsyncInput<
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
> = {
  pool: Address<TAccountPool>;
  oracle?: Address<TAccountOracle>;
  windowSeconds: OracleConsultInstructionDataArgs['windowSeconds'];
};

export async function getOracleConsultInstructionAsync<
  TAccountPool extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: OracleConsultAsyncInput<TAccountPool, TAccountOracle>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  OracleConsultInstruction<TProgramAddress, TAccountPool, TAccountOracle>
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.oracle.value) {
    accounts.oracle.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([111, 114, 97, 99, 108, 101])),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getOracleConsultInstructionDataEncoder().encode(
      args as OracleConsultInstructionDataArgs,
    ),
    programAddress,
  } as OracleConsultInstruction<TProgramAddress, TAccountPool, TAccountOracle>);
}

export type OracleConsultInput<
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
> = {
  pool: Address<TAccountPool>;
  oracle: Address<TAccountOracle>;
  windowSeconds: OracleConsultInstructionDataArgs['windowSeconds'];
};

export function getOracleConsultInstruction<
  TAccountPool extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: OracleConsultInput<TAccountPool, TAccountOracle>,
  config?: { programAddress?: TProgramAddress },
): OracleConsultInstruction<TProgramAddress, TAccountPool, TAccountOracle> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getOracleConsultInstructionDataEncoder().encode(
      args as OracleConsultInstructionDataArgs,
    ),
    programAddress,
  } as OracleConsultInstruction<TProgramAddress, TAccountPool, TAccountOracle>);
}

export type ParsedOracleConsultInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    oracle: TAccountMetas[1];
  };
  data: OracleConsultInstructionData;
};

export function parseOracleConsultInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedOracleConsultInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { pool: getNextAccount(), oracle: getNextAccount() },
    data: getOracleConsultInstructionDataDecoder().decode(instruction.data),
  };
}
